import { useDispatch, useSelector } from 'react-redux';
import setFilter from 'redux/actions/setFilter';
import { get, set, size} from 'global/lib/lodash';
import { useUser } from './useUser';
import { getItemType } from '../../feature/report/AssessmentScore';

export const useFilter = (sel) => {
  const dispatch = useDispatch();
  const filter = useSelector((state) => get(state, `filter.${sel}`));
  const user = useUser();
  const contextIds = user.siteContextSelectedRealm ? [user.siteContextSelectedRealm.id] : user.siteContextRealms.map(r => r.id);

  const changeFilter = (select, value) => {
    const [a, b] = sel.split('.');
    const fi = { ...filter };
    set(fi, select, value);
    dispatch(setFilter(a, b, fi));
  };

  const canShowDoc = (doc, edition, opts = {}) => {
    const config_doc = get(edition, `config.docs.${doc.id}`);
    if (!config_doc) { return true }
    if (!contextIds) { return true }
    return !!get(config_doc, 'realms', []).find(r => contextIds.includes(r.id))
  }

  const findAssessmentRealmDivision = (realm) => {
    if (!realm) { return {} }

    if (realm.tier === 'division') { return realm }

    const r = user.allrealms[realm.parentId];

    if (!r) { return {} };

    return findAssessmentRealmDivision(r);
  }

  const canShowChecklist = (checklist, edition, opts = {}) => {
    const docs = get(edition, `config.docs`, {})
    const docKey = Object.keys(docs).find(d => docs[d].documentableId == checklist.checklistId && docs[d].documentableType === 'checklist');
    if (!docKey) { return true }

    const doc = docs[docKey];
    const arealm = get(opts, 'realm', {});
    const hasValidChildren = get(opts, 'children', []).find(c => {
      const hasVal = c.value != null;
      const isTemp = !!c.templateScoreId;
      
      return isTemp ? !c.templateUnchanged : hasVal;
    });
    return hasValidChildren || doc.realms.find(r => r.id == arealm.id);
  }

  const filterAssessmentItems = (data, edition, opts = {}) => {
    const hasconfig = get(edition, `config.docs`);
    if (!hasconfig) { return data }
    
    const realm = findAssessmentRealmDivision(get(opts, 'assessment.data.realm'));

    return data.filter(d => {
      const type = getItemType({ ...d, children: true });
      if (type === 'checklist') {
        const children = data.filter(dd => dd.parentId == d.id);
        return canShowChecklist(d, edition, { ...opts, children, realm });
      }
      return true;
    });
  }

  const filterScorecards = (scorecards, opts = {}) => {
    opts = {
      filterByUserGroups: false,
      ...opts,
    }

    return scorecards.filter(sc => {
      const { realms, ownerRealmId, userGroups } = sc;
      const arealms = get(user, 'allrealms', {});
      
      if (!arealms[ownerRealmId]) { return true } //shared, not part of users builder

      let pass = !size(realms) ? true : realms.find(r => !!get(arealms, r.id));
      
      if (opts.filterByUserGroups && pass) {
        const usersUserGroups = get(user, 'me.userGroups', []);
        pass = !size(userGroups) ? true : !!userGroups.find(ug => usersUserGroups.find(u => u.id == ug.id));
      }

      return pass;
    });
  }

  const filterEditions = (editions, opts = {}) => {
    opts = {
      userGroups: [],
      ...opts,
    }

    return editions.filter(e => {
      const { userGroups, realms, ownerRealmId } = e;
      const arealms = get(user, 'allrealms', {});
      const isShared = !arealms[ownerRealmId];

      if (isShared) { return true }

      const { siteContextRealmTier, siteContextRealms } = user;

      let pass = !size(realms) ? true : realms.find(r => {
        const hasAccess = !!get(arealms, r.id);
        const { id, tier } = r;
        
        if (siteContextRealmTier == 'builder' || tier == 'builder') {
          return hasAccess;
        } else {
          return hasAccess && !!siteContextRealms.find(cr => cr.id === id);
        }
      });

      if (pass) { 
        pass = !size(userGroups) || !size(opts.userGroups) ? true : userGroups.find(ug => opts.userGroups.includes(`${ug.id}`))
      }

      return pass;
    });
  }

  return {
    changeFilter,
    filter,
    canShowDoc,
    canShowChecklist,
    filterAssessmentItems,
    filterScorecards,
    filterEditions,
    user,
  };
};
